import { ReportApiModel } from '../../../api/reports/model/report-api.model';
import { BlockModel } from '../../PageBuilder/models/block.model';
import { BlockTypeModel } from '../../PageBuilder/models/block-type.model';
import { ReportBlockApiModel } from '../../../api/reports/model/report-block-api.model';
import {
    reportChartInfoMap,
    ReportChartSourcesTypeEnum,
    ReportBlockTypeEnum,
    reportBlockTypeEnumFromApi,
    reportBlockTypeEnumToApi,
    reportBlockTypeEnumToDefaultChartSpecificType
} from '../enums/report-block-type.enum';
import { reportsBlockTypes } from './reports-block-types.model';
import { ChartDisplayElementModel } from './chart-display-element.model';
import { ReportSeriesModel } from './report-series.model';
import { DimensionsModel } from '../../Misc/models/dimensions.model';
import { LabelDisplayOptionsEnum } from '../../Misc/enums/label-display-options.enum';
import { SearchCriteriaQuerySentimentsEnum } from '../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { ReportDateRangeModel } from './report-date-range.model';
import { DateQueryTypeEnum } from '../../../api/mentions/enum/date-query-type.enum';
import { DateQueryModel } from '../../Forms/models/date-query.model';
import { SeriesTypeEnum } from '../enums/series-type.enum';
import { ChartStylesModel } from '../../Charts/models/chart-styles.model';
import { GoogleAnalyticsReportTypesEnum } from '../../../api/externalData/enums/google-analytics-report-types.enum';
import { ChartDisplaySettingsModel } from '../../Charts/models/chart-display-settings.model';
import { ChartMetricsEnum } from '../enums/chart-metrics.enum';
import { ChartSpecificTypesEnum } from '../enums/chart-specific-types.enum';
import { ChartBlockComponent } from '../components/ChartBlock/chart-block.component';
import { MapChartColorSchemeEnum } from '../../Charts/models/map-chart.model';
import { Mention } from '../../Mentions/models/mention';
import { SearchCriteriaSortFieldEnum } from '../../../api/mentions/enum/search-criteria-sort-field.enum';
import { SortableMentionBlockTypeEnum } from '../enums/sortable-mention-block-type.enum';
import { MentionCustomizationModel } from '../../../Monitor/Shared/models/mention-customization.model';
import { ReportBlockTypeApiEnum } from '../../../api/reports/enum/report-block-type-api.enum';
import { CoverageMetricChartDisplaySettings } from '@Common/Charts/models/coverage-metric-chart-display-settings.model';
import { CoverageMetricChartDisplayElement } from './coverage-metric-chart-display-element.model';
import { CoverageMetricChartStyles } from '@Common/Charts/models/coverage-metric-chart-styles.model';
import { ChartAnnotations } from './chart-annotations.model';
import { annotatableBlockTypes } from './new-data-processing-workflow-block-types.model';


export class ReportModel {
    id: number;
    name: string;
    title: string;
    blocks: BlockModel[];
    createdDate: number;
    modifiedByUserId: number;
    modifiedDate: number;
    exportImageUrl: string;
    globalDateRange: any;
    globalSourceSingleSeries: ReportSeriesModel;
    globalSourceMultiSeries: ReportSeriesModel[];
    hidden: boolean;
    accountId: number;
    userId: number;
    groupId?: number;

    private simpleCompareFields = [
        'id',
        'name',
        'title',
        'exportImageUrl'
    ];

    /**
     * Class constructor
     */
    constructor(apiModel?: ReportApiModel) {
        if (apiModel) {
            this.id = apiModel.id;
            this.name = apiModel.name;
            this.hidden = apiModel.hidden;
            this.accountId = apiModel.accountId;
            this.userId = apiModel.userId;
            this.createdDate = apiModel.createdDate;
            if (apiModel.lastModifiedByUserId) {
                this.modifiedByUserId = apiModel.lastModifiedByUserId;
            }
            if (apiModel.lastModifiedDate) {
                this.modifiedDate = apiModel.lastModifiedDate;
            }
            // @TODO - remove hack as part of NEP-1772
            if (apiModel.lastModifiedByUserId === 0) {
                this.modifiedByUserId = null;
            }
            // @TODO - remove hack as part of NEP-1772
            if (apiModel.lastModifiedDate === 0) {
                this.modifiedDate = null;
            }

            const data = JSON.parse(apiModel.data);
            this.title = data.title;
            this.blocks = this.mapBlocksFromApi(data.blocks);
            this.exportImageUrl = data.imageUrl;
            this.globalSourceSingleSeries = new ReportSeriesModel(data.globalSourceSingleSeries);
            this.globalSourceMultiSeries = (data.globalSourceMultiSeries || []).map(series => new ReportSeriesModel(series));
            this.groupId = apiModel.displayGroupId;
        } else {
            this.id = null;
            this.name = null;
            this.title = null;
            this.blocks = [];
            this.accountId = null;
            this.userId = null;
            this.createdDate = null;
            this.modifiedByUserId = null;
            this.modifiedDate = null;
            this.exportImageUrl = null;
            this.globalSourceSingleSeries = null;
            this.globalSourceMultiSeries = null;
            this.hidden = null;
        }
    }

    /**
     * Converts this front end model to the back end version
     */
    public toApiModel(anonymize: boolean = false): ReportApiModel {
        return {
            id: this.id,
            name: this.name,
            createdDate: this.createdDate,
            lastModifiedByUserId: this.modifiedByUserId,
            lastModifiedDate: this.modifiedDate,
            hidden: this.hidden,
            accountId: this.accountId,
            userId: this.userId,
            data: JSON.stringify({
                title: this.title,
                blocks: this.mapBlocksToApi(this.blocks),
                imageUrl: this.exportImageUrl,
                globalSourceSingleSeries: this.globalSourceSingleSeries?.toApiModel(anonymize),
                globalSourceMultiSeries: (this.globalSourceMultiSeries || []).map(series => series.toApiModel(anonymize)),
            }),
            displayGroupId: this.groupId
        };
    }

    /**
     * Checks if the name has been filled out
     */
    public hasName(): boolean {
        const whitespace: RegExp = /\s/g;
        const isNameSet: boolean = this.name !== null && this.name !== undefined;
        const isNameComplete: boolean = isNameSet && this.name.replace(whitespace, '').length > 0;
        const hasName: boolean = isNameSet && isNameComplete;

        return hasName;
    }

    /**
     * Deep compare against another report model
     */
    public equals(other: ReportModel): boolean {
        if (!other) {
            return false;
        }
        // Compare simple fields
        for (let i = 0; i < this.simpleCompareFields.length; i++) {
            const field = this.simpleCompareFields[i];
            if (this[field] !== other[field]) {
                return false;
            }
        }
        // Compare blocks
        if (this.blocks.length !== other.blocks.length) {
            return false;
        }
        for (let i = 0; i < this.blocks.length; i++) {
            const thisBlock = this.blocks[i];

            // report block order may be changed by page builder component for accessibility concern
            // so here we find by id
            const otherBlock = other.blocks.find(x => thisBlock.id === x.id);

            if (!otherBlock) {
                return false;
            }

            if (thisBlock.id !== otherBlock.id) {
                return false;
            }
            if (!thisBlock.type || !otherBlock.type || thisBlock.type.key !== otherBlock.type.key) {
                return false;
            }
            if (thisBlock.x !== otherBlock.x) {
                return false;
            }
            if (thisBlock.y !== otherBlock.y) {
                return false;
            }
            if (thisBlock.rows !== otherBlock.rows) {
                return false;
            }
            if (thisBlock.cols !== otherBlock.cols) {
                return false;
            }

            if (!thisBlock.displayElement.isEqual(otherBlock.displayElement)) {
                return false;
            }
        }

        return true;
    }

    /**
     * Get the blocks in order of left -> right, top -> bottom
     */
    public getBlocksInVisualOrder(): BlockModel[] {
        const blocks: BlockModel[] = this.blocks.slice();

        blocks.sort(
            (a, b) => {
                // y location takes precedence, if it's on a lower row, it appears later
                if (a.y < b.y) {
                    return -1;
                } if (a.y > b.y) {
                    return 1;
                } else {
                    // They're on the same level, need to compare x location
                    if (a.x < b.x) {
                        return -1;
                    } else if (a.x > b.x) {
                        return 1;
                    } else {
                        // This case should be impossible, it means that both blocks have the same co-ordinates
                        return 0;
                    }
                }
            }
        );

        return blocks;
    }

    public getDimensions(): DimensionsModel {
        const dimensions: DimensionsModel = new DimensionsModel(0, 0);

        this.blocks.forEach((block) => {
            const endX = block.x + block.cols;
            const endY = block.y + block.rows;

            if (!endX || !endY) {
                return;
            }

            dimensions.width = Math.max(dimensions.width, endX);
            dimensions.height = Math.max(dimensions.height, endY);
        });

        return dimensions;
    }

    /**
     * Returns a clone of this ReportModel
     * @param anonymize - when true, reset as if it were not yet saved
     * @param anonymizeMentions - when true, creates new unique saved-mention search sources,
     * which is needed for decoupling the sources for operations such as saveAs, and is done by resetting the ids of all ReportSeriesMentionModels.
     */
    public clone(anonymize: boolean = true, anonymizeMentions: boolean = false): ReportModel {
        const clone = new ReportModel(this.toApiModel(anonymizeMentions));

        // toApiModel doesn't handle filters on series, so we need to copy those over manually
        for (let blockIndex = 0; blockIndex < this.blocks.length; blockIndex++) {
            const origBlockDisplayElement = this.blocks[blockIndex].displayElement as ChartDisplayElementModel;
            for (let seriesIndex = 0; seriesIndex < origBlockDisplayElement._series.length; seriesIndex++) {
                const origBlockSeries = origBlockDisplayElement._series[seriesIndex];
                if (origBlockSeries.mentions.filters) {
                    const cloneDisplayElement = clone.blocks[blockIndex].displayElement as ChartDisplayElementModel;
                    cloneDisplayElement._series[seriesIndex].mentions.filters = origBlockSeries.mentions.filters.clone(anonymize);
                    if (anonymizeMentions) {
                        cloneDisplayElement._series[seriesIndex].mentions.searchCriteriaId = null;
                        cloneDisplayElement._series[seriesIndex].id = null;
                    }
                }
            }
        }

        if (anonymizeMentions) {
            clone.globalSourceSingleSeries.mentions.filters = this.globalSourceSingleSeries.mentions.filters;
            if (clone.globalSourceSingleSeries.mentions.filters) {
                clone.globalSourceSingleSeries.mentions.filters.id = null;
                clone.globalSourceSingleSeries.mentions.filters.name = null;
            }

            clone.globalSourceMultiSeries.forEach((reportSeries, i) => {
                reportSeries.mentions.filters = this.globalSourceMultiSeries[i].mentions.filters;
                if (reportSeries.mentions.filters) {
                    reportSeries.mentions.filters.id = null;
                    reportSeries.mentions.filters.name = null;
                }
            });
        }

        if (anonymize) {
            delete clone.id;
            clone.userId = null;
            clone.createdDate = null;
            clone.modifiedByUserId = null;
            clone.modifiedDate = null;
            clone.name = null;
        }

        return clone;
    }

    /**
     * Converts ReportBlockApiModel[] to BlockModel[]
     */
    protected mapBlocksFromApi(apiBlocks: ReportBlockApiModel[]): BlockModel[] {
        const blocks: BlockModel[] = [];

        const blockTypeMap: Map<ReportBlockTypeEnum, BlockTypeModel> = this.makeBlockTypeMap(reportsBlockTypes);

        for (const apiBlock of apiBlocks) {
            const blockType = blockTypeMap.get(reportBlockTypeEnumFromApi.get(apiBlock.type));
            if (!blockType) {
                // should not get here, useful for debugging
                console.log('block type not recognized');
                continue;
            }

            const block: BlockModel = new BlockModel(
                apiBlock.id,
                blockType,
                new (blockType.displayElementType)()
            );

            block.rows = apiBlock.height;
            block.cols = apiBlock.width;
            block.x = apiBlock.xPosition;
            block.y = apiBlock.yPosition;
            block.canAnnotate = annotatableBlockTypes.includes(blockType.key as ReportBlockTypeEnum);

            if (reportBlockTypeEnumFromApi.has(apiBlock.type)) {
                const chartDisplayElement = block.displayElement as ChartDisplayElementModel;
                const data = new ChartStylesModel(apiBlock.data);
                chartDisplayElement.metric = data.metric;
                chartDisplayElement.metrics = data.metrics;
                this.setFallBackValuesForOldCharts(chartDisplayElement, data, apiBlock, block);

                chartDisplayElement.sentiments = data.sentiments;
                chartDisplayElement.labelDisplayOptions = data.labelDisplayOptions;
                chartDisplayElement.stackedChartTypes = data.stackedChartTypes;
                chartDisplayElement.referralTypes = data.referralTypes;
                chartDisplayElement.prevents = data.prevents;
                if (data.termChartType && !data.chartSpecificType) {
                    // back compatibility
                    chartDisplayElement.chartSpecificType = data.termChartType;
                } else {
                    chartDisplayElement.chartSpecificType = data.chartSpecificType;
                }

                if (!chartDisplayElement.chartSpecificType) {
                    chartDisplayElement.chartSpecificType = reportBlockTypeEnumToDefaultChartSpecificType.get(block.type.key as ReportBlockTypeEnum);
                }

                chartDisplayElement.termType = data.termType;
                chartDisplayElement.numOfItems = data.numOfItems;
                if (chartDisplayElement.chartSpecificType === ChartSpecificTypesEnum.MAP) {
                    chartDisplayElement.mapChartColorScheme = data.mapChartColorScheme || MapChartColorSchemeEnum.REGION_COLORS;
                }
                chartDisplayElement.mapChartChoroplethInterpolationType = data.mapChartChoroplethInterpolationType;
                chartDisplayElement.chartHeaderSettings = new ChartDisplaySettingsModel(data.chartDisplaySettings);
                chartDisplayElement.timeInterval = data.timeInterval;
                chartDisplayElement.topMentionsSortField = data.topMentionsSortField;
                chartDisplayElement.topMentionsMetadata = new MentionCustomizationModel(data.topMentionsMetadata);
                chartDisplayElement.topCuratedMentions = data.topCuratedMentions?.map(x => new Mention(x)) || [];
                chartDisplayElement.displayAnnotationInExport = data.displayAnnotationInExport;
                chartDisplayElement.chartAnnotations = new ChartAnnotations(data.chartAnnotations?.indicators, data.chartAnnotations?.markerState);

                if (apiBlock.type === ReportBlockTypeApiEnum.COVERAGE_METRIC) {
                    chartDisplayElement.chartHeaderSettings = new CoverageMetricChartDisplaySettings(data.chartHeaderSettings);
                    (chartDisplayElement as CoverageMetricChartDisplayElement).titleIcon = data.titleIcon;
                    (chartDisplayElement as CoverageMetricChartDisplayElement).customCurrentValue = data.customCurrentValue;
                }

                chartDisplayElement.series = apiBlock.series.map(
                    reportApiSeriesModel => new ReportSeriesModel(
                        reportApiSeriesModel,
                        reportBlockTypeEnumFromApi.get(apiBlock.type),
                        null,
                        data.metric,
                        chartDisplayElement
                    )
                );

                if (SortableMentionBlockTypeEnum[block.type.key]) {
                    // top mentions custom only need mentions no facets, so it won't be retrieved for facet search
                    if (chartDisplayElement.topMentionsSortField !== SearchCriteriaSortFieldEnum.custom) {
                        chartDisplayElement.series.forEach(x => x.sortField = chartDisplayElement.topMentionsSortField);
                    }
                }

                if (chartDisplayElement.chartSpecificType === ChartSpecificTypesEnum.WORD_CLOUD) {
                    block.type = block.type.clone();
                    block.type.componentType = ChartBlockComponent;
                }

                for (const series of chartDisplayElement.series) {
                    if (data.sentiments) {
                        series.sentiments = data.sentiments;
                    }
                }

                // set the color property of each series, if it exists in the colormap
                for (let i = 0; i < chartDisplayElement.series.length; i++) {
                    const color = data.colorMap[ReportSeriesModel.getSeriesColorKeyPrefix() + i];

                    if (color) {
                        chartDisplayElement.series[i].color = color;
                    }
                }

                // if the data is older it won't contain options for sentiment or label display
                // provide the defaults here:
                if (data.labelDisplayOptions === undefined) {
                    chartDisplayElement.labelDisplayOptions = [
                        LabelDisplayOptionsEnum.percent
                    ];
                }

                if (!data.sentiments) {
                    chartDisplayElement.sentiments = [
                        SearchCriteriaQuerySentimentsEnum.positive,
                        SearchCriteriaQuerySentimentsEnum.neutral,
                        SearchCriteriaQuerySentimentsEnum.negative,
                        SearchCriteriaQuerySentimentsEnum.notAvailable
                    ];
                }

                if (!data.referralTypes) {
                    if (block.type.key === ReportBlockTypeEnum.COVERAGE_IMPACT_TREND) {
                        chartDisplayElement.referralTypes = [
                            GoogleAnalyticsReportTypesEnum.UNIQUE_PAGEVIEWS,
                            GoogleAnalyticsReportTypesEnum.SESSIONS,
                            GoogleAnalyticsReportTypesEnum.USERS
                        ];
                    } else if (block.type.key === ReportBlockTypeEnum.REFERRAL_TREND) {
                        chartDisplayElement.referralTypes = [
                            GoogleAnalyticsReportTypesEnum.TOTAL_REFERRALS,
                            GoogleAnalyticsReportTypesEnum.WEB_REFERRALS,
                            GoogleAnalyticsReportTypesEnum.SOCIAL_REFERRALS,
                            GoogleAnalyticsReportTypesEnum.EMAIL_REFERRALS
                        ];
                    }
                }

                // label display options were briefly stored directly on the 'checkboxOptions' property instead
                // of on 'labelDisplayOptions' - this is a compatibility layer
                if (data.checkboxOptions && data.checkboxOptions.includes(LabelDisplayOptionsEnum.percent)) {
                    chartDisplayElement.labelDisplayOptions.push(LabelDisplayOptionsEnum.percent);
                    data.checkboxOptions.splice(data.checkboxOptions.indexOf(LabelDisplayOptionsEnum.percent), 1);
                }

                if (data.checkboxOptions && data.checkboxOptions.includes(LabelDisplayOptionsEnum.count)) {
                    chartDisplayElement.labelDisplayOptions.push(LabelDisplayOptionsEnum.count);
                    data.checkboxOptions.splice(data.checkboxOptions.indexOf(LabelDisplayOptionsEnum.count), 1);
                }

                delete data.sentiments;
                delete data.labelDisplayOptions;
                delete data.referralTypes;
                delete data.prevents;
                delete data.chartAnnotations;

                chartDisplayElement.styles = new ChartStylesModel(data);
                if (apiBlock.type === ReportBlockTypeApiEnum.COVERAGE_METRIC) {
                    chartDisplayElement.styles = new CoverageMetricChartStyles(data);
                    chartDisplayElement.styles.chartHeaderSettings = new CoverageMetricChartDisplaySettings(data.chartHeaderSettings);
                    chartDisplayElement.styles.chartDisplaySettings = chartDisplayElement.styles.chartHeaderSettings;
                }

                chartDisplayElement.title = apiBlock.title;
                chartDisplayElement.size = new DimensionsModel(block.rows, block.cols);
                block.displayElement = chartDisplayElement;
            }

            blocks.push(block);
        }
        return blocks;
    }

    /**
     * Converts BlockModel[] to ReportBlockApiModel[]
     */
    protected mapBlocksToApi(blocks: BlockModel[]): ReportBlockApiModel[] {
        const apiBlocks: ReportBlockApiModel[] = [];

        for (const block of blocks) {
            if (!block.type || !block.type.key) {

                return;
            }

            if (reportBlockTypeEnumToApi.has(block.type.key as ReportBlockTypeEnum)) {
                const chartDisplayElement = block.displayElement as ChartDisplayElementModel;

                const data = chartDisplayElement.styles;
                data.sentiments = chartDisplayElement.sentiments;
                data.labelDisplayOptions = chartDisplayElement.labelDisplayOptions;
                data.stackedChartTypes = chartDisplayElement.stackedChartTypes;
                data.metric = chartDisplayElement.metric;
                data.metrics = chartDisplayElement.metrics;
                data.referralTypes = chartDisplayElement.referralTypes;
                data.prevents = chartDisplayElement.prevents;
                data.chartSpecificType = chartDisplayElement.chartSpecificType;
                data.termType = chartDisplayElement.termType;
                data.numOfItems = chartDisplayElement.numOfItems;
                data.mapChartColorScheme = chartDisplayElement.mapChartColorScheme;
                data.mapChartChoroplethInterpolationType = chartDisplayElement.mapChartChoroplethInterpolationType;
                data.topMentionsSortField = chartDisplayElement.topMentionsSortField;
                data.topMentionsMetadata = chartDisplayElement.topMentionsMetadata;
                data.topCuratedMentions = chartDisplayElement.topCuratedMentions?.map(x => x.convertToApiMention());
                data.chartDisplaySettings = chartDisplayElement.chartHeaderSettings;
                data.chartHeaderSettings =  data.chartDisplaySettings;
                data.timeInterval = chartDisplayElement.timeInterval;
                data.displayAnnotationInExport = chartDisplayElement.displayAnnotationInExport;
                data.chartAnnotations = chartDisplayElement.chartAnnotations?.clone();

                if (block.type.key === ReportBlockTypeEnum.COVERAGE_METRIC) {
                    data.titleIcon = (chartDisplayElement as CoverageMetricChartDisplayElement).titleIcon;
                    data.customCurrentValue = (chartDisplayElement as CoverageMetricChartDisplayElement).customCurrentValue;
                }


                // there can only be one color associated with this series, so delete all other colors that
                // look like they are meant to associate with *a* series
                // we'll set the colors just below, per series
                for (const [key, _] of Object.entries(data.colorMap)) {
                    if (key.startsWith(ReportSeriesModel.getSeriesColorKeyPrefix())) {
                        delete data.colorMap[key];
                    }
                }

                for (let i = 0; i < chartDisplayElement.series.length; i++) {
                    const series = chartDisplayElement.series[i];
                    const seriesKey = ReportSeriesModel.getSeriesColorKeyPrefix() + i;

                    // actually set the color for the series in the colorMap, if appropriate
                    if (series.color) {
                        data.colorMap[seriesKey] = series.color;
                    }
                }

                apiBlocks.push({
                    id: block.id,
                    type: reportBlockTypeEnumToApi.get(block.type.key as ReportBlockTypeEnum),
                    title: chartDisplayElement.title,
                    xPosition: block.x,
                    yPosition: block.y,
                    width: block.cols,
                    height: block.rows,
                    data: JSON.stringify(data.toApi()),
                    series: (chartDisplayElement.series || []).map(series => series.toApiModel())
                } as ReportBlockApiModel);

                // because data = displayElement.styles above, some logic changes data which changes this.block.displayElement.styles
                // so we need to remove some from data after the block has been pushed
                // when you add a line here please also add the coresponding line in fromApi
                delete data.sentiments;
                delete data.labelDisplayOptions;
                delete data.referralTypes;
                delete data.prevents;
                delete data.chartAnnotations;
            }

            if (!block.id) {
                delete block.id;
            }
        }

        return apiBlocks;
    }

    /**
     * Gets all the ReportSeriesModels from nested block display elements
     */
    public getSeries(): ReportSeriesModel[] {
        const series: ReportSeriesModel[] = [];

        for (const block of this.blocks) {
            if ((block.displayElement as ChartDisplayElementModel)?.series) {
                series.push(...(block.displayElement as ChartDisplayElementModel).series);
            }
        }

        return series;
    }

    public getMentionsSeries(): ReportSeriesModel[] {
        const series: ReportSeriesModel[] = [];

        for (const block of this.blocks) {
            const displayElement = block.displayElement as ChartDisplayElementModel;
            if (displayElement) {
                const mentionSeries = displayElement.getMentionSeries();
                if (mentionSeries) {
                    mentionSeries.forEach(x => x.timeInterval = displayElement.timeInterval);
                    series.push(...mentionSeries);
                }
            }
        }

        return series;
    }
    public getSocialSeries(): ReportSeriesModel[] {
        const series: ReportSeriesModel[] = [];

        for (const block of this.blocks) {
            const displayElement = block.displayElement as ChartDisplayElementModel;
            const socialSeries = displayElement?.getSocialSeries();
            if (socialSeries) {
                socialSeries.forEach(x => x.timeInterval = displayElement.timeInterval);
                series.push(...socialSeries);
            }
        }

        return series;
    }
    public getExternalDataSeries(): ReportSeriesModel[] {
        const series: ReportSeriesModel[] = [];

        for (const block of this.blocks) {
            const externalDataSeries = (block.displayElement as ChartDisplayElementModel)?.getExternalDataSeries();
            if (externalDataSeries) {
                series.push(...externalDataSeries);
            }
        }

        return series;
    }

    public setGlobalMentionSeriesByType(
        series: ReportSeriesModel[],
        type: ReportChartSourcesTypeEnum,
        globalSeries: ReportSeriesModel[],
        updateAll: boolean
    ): void {
        for (const block of this.blocks) {
            const displayElement: ChartDisplayElementModel = block.displayElement as ChartDisplayElementModel;
            const mentionSeries = displayElement.getMentionSeries();
            const socialSeries = displayElement.getSocialSeries();

            if (mentionSeries && mentionSeries[0] && reportChartInfoMap.get(mentionSeries[0].chartType).mentions === type) {
                // if user chooses to only update the charts that use global source, for those that do not use that, skip iteration
                if (!updateAll && !this.isSeriesListEqual(mentionSeries, globalSeries)){
                    continue;
                }

                if (this.canSetGlobalSeries(mentionSeries, series)) {
                    const cloneSeries = series.map((seriesItem, index) => {
                        const clone = seriesItem.clone();
                        if (mentionSeries[index]) {
                            clone.updateGlobalSource(mentionSeries[index], true);
                        } else {
                            clone.updateGlobalSource(mentionSeries[0], false);
                        }
                        return clone;
                    });
                    displayElement.setMentionSeries(cloneSeries);
                }
            }

            if (socialSeries && socialSeries[0] && reportChartInfoMap.get(socialSeries[0].chartType).mentions === type) {
                // if user chooses to only update the charts that use global source, for those that do not use that, skip iteration
                if (!updateAll && !this.isSeriesListEqual(socialSeries, globalSeries)){
                    continue;
                }

                if (this.canSetGlobalSeries(socialSeries, series)) {
                    const cloneSeries = series.map((seriesItem, index) => {
                        const clone = seriesItem.clone();
                        if (socialSeries[index]) {
                            clone.updateGlobalSource(socialSeries[index], true);
                        } else {
                            clone.updateGlobalSource(socialSeries[0], false);
                        }
                        return clone;
                    });
                    displayElement.setSocialSeries(cloneSeries);
                }
            }
        }
    }

    public getIncompatibleChartTitlesByTypeList(
        series: ReportSeriesModel[],
        type: ReportChartSourcesTypeEnum
    ): string[] {
        const titlesOfCharts: string[] = [];
        for (const block of this.blocks) {
            const displayElement: ChartDisplayElementModel = block.displayElement as ChartDisplayElementModel;
            const mentionSeries = displayElement.getMentionSeries();
            const socialSeries = displayElement.getSocialSeries();

            if (mentionSeries && mentionSeries[0] && reportChartInfoMap.get(mentionSeries[0].chartType).mentions === type) {
                if (!this.canSetGlobalSeries(mentionSeries, series)) {
                    titlesOfCharts.push(displayElement.title);
                }
            }

            if (socialSeries && socialSeries[0] && reportChartInfoMap.get(socialSeries[0].chartType).mentions === type) {
                if (!this.canSetGlobalSeries(socialSeries, series)) {
                    titlesOfCharts.push(displayElement.title);
                }
            }
        }
        return titlesOfCharts;
    }

    public getIncompatibleTitlesForDefaultSeriesByType(
        globalSeries: ReportSeriesModel[],
        type: ReportChartSourcesTypeEnum
    ): string[] {
        const titlesOfCharts: string[] = [];
        for (const block of this.blocks) {
            const displayElement: ChartDisplayElementModel = block.displayElement as ChartDisplayElementModel;
            const mentionSeries = displayElement.getMentionSeries();
            const socialSeries = displayElement.getSocialSeries();

            if (mentionSeries && mentionSeries[0] && reportChartInfoMap.get(mentionSeries[0].chartType).mentions === type) {
                if (!this.isSeriesListEqual(mentionSeries, globalSeries)){
                    titlesOfCharts.push(displayElement.title);
                }

            }

            if (socialSeries && socialSeries[0] && reportChartInfoMap.get(socialSeries[0].chartType).mentions === type) {
                if (!this.isSeriesListEqual(socialSeries, globalSeries)){
                    titlesOfCharts.push(displayElement.title);
                }
            }
        }
        return titlesOfCharts;
    }

    /**
     * Set global source for charts that use single seriers (data source)
     */
    public setGlobalMentionSingleSeries(series: ReportSeriesModel[], updateAll: boolean): void {
        return this.setGlobalMentionSeriesByType(series, ReportChartSourcesTypeEnum.single, [this.globalSourceSingleSeries], updateAll);
    }
    /**
     * Set global source for charts that use multi seriers (data source)
     */
    public setGlobalMentionMultiSeries(series: ReportSeriesModel[], updateAll: boolean): void {
        return this.setGlobalMentionSeriesByType(series, ReportChartSourcesTypeEnum.multi, this.globalSourceMultiSeries, updateAll);
    }

    public getTitlesforIncompatibleSingleSeries(series: ReportSeriesModel[]): string[] {
        return this.getIncompatibleChartTitlesByTypeList(series, ReportChartSourcesTypeEnum.single);
    }

    public getTitlesforIncompatibleMultiSeries(series: ReportSeriesModel[]): string[] {
        return this.getIncompatibleChartTitlesByTypeList(series, ReportChartSourcesTypeEnum.multi);
    }

    public getTitlesforDefaultSingleSeries(): string[] {
        return this.getIncompatibleTitlesForDefaultSeriesByType([this.globalSourceSingleSeries], ReportChartSourcesTypeEnum.single);
    }

    public getTitlesforDefaultMultiSeries(): string[] {
        return this.getIncompatibleTitlesForDefaultSeriesByType(this.globalSourceMultiSeries, ReportChartSourcesTypeEnum.multi);
    }

    private isSeriesListEqual(a: ReportSeriesModel[], b: ReportSeriesModel[]): boolean {
        if (a.length !== b.length) {
            return false;
        } else {
            for (let index = 0; index < a.length; index++) {
                if (!a[index].isEqual(b[index], true)) {
                    return false;
                }
            }
        }

        return true;
    }

    private allMentionSeriesOfTypeMatch(type: ReportChartSourcesTypeEnum, globalSeries: ReportSeriesModel[]): boolean {
        let result: boolean = true;

        this.getChartsBySourcesType(type).forEach(block => {
            if (!this.isSeriesListEqual((block.displayElement as ChartDisplayElementModel).getMentionSeries(), globalSeries)) {
                result = false;
            }
        });

        return result;
    }

    /**
     * check whether all single charts associated with the edited global source type are currently using the global source.
     * @returns true if all single charts associated with the edited global source type are currently using the global source.
     */
    public isAllUseGlobalMentionSingleSource(): boolean {
        return this.allMentionSeriesOfTypeMatch(ReportChartSourcesTypeEnum.single, [this.globalSourceSingleSeries]);
    }

    /**
     * check whether all comparison charts associated with the edited global source type are currently using the global source.
     * @returns true if all comparison charts associated with the edited global source type are currently using the global source.
     */
    public isAllUseGlobalMentionMultiSource(): boolean {
        return this.allMentionSeriesOfTypeMatch(ReportChartSourcesTypeEnum.multi, this.globalSourceMultiSeries);
    }

    public getChartsBySourcesType(type: ReportChartSourcesTypeEnum): BlockModel[] {
        return this.blocks.filter(block => {
            return reportChartInfoMap.get(block.type.key as ReportBlockTypeEnum).mentions === type;
        });
    }

    /**
     * get all the charts that use single source
     * @returns all the charts that use single source
     */
    public getSingleSourceCharts(): BlockModel[] {
        return this.getChartsBySourcesType(ReportChartSourcesTypeEnum.single);
    }

    /**
     * get all the charts that use multi source
     * @returns all the charts that use multi source
     */
    public getMultiSourceCharts(): BlockModel[] {
        return this.getChartsBySourcesType(ReportChartSourcesTypeEnum.multi);
    }

    /**
     * when the current report has charts with date range "ALL", return false if subscription or topic is selected in the global source
     * media type and Top 10 Outlets charts cannot use subscription as source
     * prominence charts cannot use topic, subscription and source group as source
     */
    canSetGlobalSeries(originalSeries: ReportSeriesModel[], globalSeries: ReportSeriesModel[]): boolean {
        let hasSubscription = false;
        let hasTopic = false;
        let hasSocial = false;
        let hasSavedMention = false;

        const chartType = originalSeries[0]?.chartType;
        if (!chartType) {
            return false;
        }

        globalSeries.map(seriesItem => {
            if (seriesItem.type === SeriesTypeEnum.subscription ) {
                hasSubscription = true;
            } else if (seriesItem.type === SeriesTypeEnum.topic) {
                hasTopic = true;
            } else if (seriesItem.hasValidSavedMentionSource()) {
                hasSavedMention = true;
            } else if (seriesItem.hasValidSocialSource()) {
                hasSocial = true;
            }
        });

        let canSetGlobalSeries = !(DateQueryModel.equals(originalSeries[0].dateRange, DateQueryModel.getAllQuery()) &&
            (hasTopic || hasSubscription || hasSocial));

        if (hasSubscription &&
            (chartType === ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN
            || chartType === ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN_COMPARISON
            || chartType ===  ReportBlockTypeEnum.TOP_10_OUTLETS)) {
            canSetGlobalSeries = false;
        }

        // if the global setting has social source selected.. do not allow these charts
        if (hasSocial &&
            (chartType === ReportBlockTypeEnum.TOP_10_OUTLETS
            || chartType === ReportBlockTypeEnum.PROMINENCE_OVER_TIME
            || chartType ===  ReportBlockTypeEnum.PROMINENCE_BY_REACH
            || chartType === ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN_COMPARISON
            || chartType ===  ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN
            || chartType === ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS
            || chartType ===  ReportBlockTypeEnum.TOP_REFERRAL_SOURCES
            || chartType ===  ReportBlockTypeEnum.REFERRAL_TREND)) {
            canSetGlobalSeries = false;
        }

        // do not use global source for prominence charts
        if (chartType === ReportBlockTypeEnum.PROMINENCE_BY_REACH || chartType === ReportBlockTypeEnum.PROMINENCE_OVER_TIME) {
            canSetGlobalSeries = false;
        }

        return canSetGlobalSeries;
    }

    /**
     * Returns the blocks of charts with a date range "all"
     */
    getBlocksWithDateRangeAll(): BlockModel[] {
        const chartBlocksWithDateRangeAll = this.blocks.filter(block => {
            const chartBlockDisplayElement = block.displayElement as ChartDisplayElementModel;
            if (!chartBlockDisplayElement || !chartBlockDisplayElement.series) {
                return;
            }

            return !!chartBlockDisplayElement.series.find(seriesItem => DateQueryModel.equals(seriesItem.dateRange, DateQueryModel.getAllQuery()));
        });

        return chartBlocksWithDateRangeAll;
    }

    /**
     * Sets the provided date range to all compatible charts
     */
    public setGlobalDateRange(date: ReportDateRangeModel, selectedDateOutsideRange: boolean = false): void {
        this.globalDateRange = date;

        for (const block of this.blocks) {
            const chartBlockDisplayElement = block.displayElement as ChartDisplayElementModel;

            if (chartBlockDisplayElement && chartBlockDisplayElement.series) {
                if (this.globalDateRange.type === DateQueryTypeEnum.ALL || selectedDateOutsideRange) {
                    const incompatibleSource = chartBlockDisplayElement.series.find(seriesItem => !seriesItem.canUseAllDateRange());

                    if (incompatibleSource) {
                        continue;
                    }
                }

                // Referral trend chart does not support last 24 hours date range, skip it when setting global date range
                if (this.globalDateRange.type === DateQueryTypeEnum.RELATIVE
                    && this.globalDateRange.relativeStartOffsetH === -24
                    && block.type.key === ReportBlockTypeEnum.REFERRAL_TREND ) {

                    continue;
                }
                chartBlockDisplayElement.series.forEach(seriesItem => {
                    // clear data, otherwise, it would subscribe to old data and then subscribe to new data
                    seriesItem.clearData();
                    seriesItem.dateRange = this.globalDateRange;
                });

                if (this.globalDateRange.type === DateQueryTypeEnum.ALL && chartBlockDisplayElement.chartHeaderSettings?.previousPeriod) {
                    chartBlockDisplayElement.chartHeaderSettings.previousPeriod = false;
                }
            }
        }
    }

    /**
     * Returns true if all the series have the same dateRange selected
     */
    isGlobalDateSet(): boolean {
        const series = this.getSeries();

        if (!series || !series.length) {
            return;
        }

        return !series.find(seriesItem => !seriesItem.dateRange.isEqual(series[0].dateRange));
    }

    public hasChartOfSourceType(type: ReportChartSourcesTypeEnum): boolean {
        return !!this.blocks.find(block => {
            return reportChartInfoMap.get(block.type.key as ReportBlockTypeEnum).mentions === type;
        });
    }

    /**
     * Returns true if the report contains the series that use single data source
     */
    hasSingleSeries(): boolean {
        return this.hasChartOfSourceType(ReportChartSourcesTypeEnum.single);
    }

    /**
     * Returns true if the report contains the series (comparison charts) that use multi series
     */
    hasMultiSeries(): boolean {
        return this.hasChartOfSourceType(ReportChartSourcesTypeEnum.multi);
    }

    /**
     * Returns true if the a series on the report requires the date option "ALL"
     */
    anySeriesAllowsAllDate(): boolean {
        return !!this.getSeries().find(seriesItem => seriesItem.canUseAllDateRange());
    }

    getChartsThatCanNotUseAllDate(): BlockModel[] {
        return this.blocks.filter(block => {
            const chartBlockDisplayElement = block.displayElement as ChartDisplayElementModel;
            return chartBlockDisplayElement.series.find(seriesItem => !seriesItem.canUseAllDateRange());
        });
    }

    getChartsThatCanNotUseLast24Hours(): BlockModel[] {
        return this.blocks.filter(block => {
            const chartBlockDisplayElement = block.displayElement as ChartDisplayElementModel;
            return chartBlockDisplayElement.series.find(seriesItem => !seriesItem.canUseLast24HoursDateRange());
        });
    }

    getChartsThatHaveUnsavedSources(): BlockModel[] {
        return this.blocks.filter(block => {
            const chartBlockDisplayElement = block.displayElement as ChartDisplayElementModel;
            return chartBlockDisplayElement.series.find(seriesItem => seriesItem.isUnsavedSource());
        });
    }

    /**
     * Converts the BlockTypeModel[] into a map for lookup efficiency
     */
    protected makeBlockTypeMap(blockTypes: BlockTypeModel[]): Map<ReportBlockTypeEnum, BlockTypeModel> {
        const map = new Map<ReportBlockTypeEnum, BlockTypeModel>();

        blockTypes.forEach(
            blockType => {
                map.set(blockType.key as ReportBlockTypeEnum, blockType);
            }
        );

        return map;
    }

    // Backwards compatible fall back values for old charts with no values
    setFallBackValuesForOldCharts(
        chartDisplayElement: ChartDisplayElementModel,
        data: ChartStylesModel,
        apiBlock: ReportBlockApiModel,
        block: BlockModel
    ): void {
        if (!data.metric) {
            if (apiBlock.type === ReportBlockTypeApiEnum.AD_VALUE_EQUIVILANCY_COMPARISON) {
                chartDisplayElement.metric = ChartMetricsEnum.ave;
            }
            if (apiBlock.type === ReportBlockTypeApiEnum.AUDIENCE_REACH_OVER_TIME_COMPARISON) {
                chartDisplayElement.metric = ChartMetricsEnum.IMPRESSIONS;
            }
        }
        if (!data.metrics || data.metrics.length === 0) {
            if (block.type.key === ReportBlockTypeEnum.COVERAGE_SUMMARY) {
                chartDisplayElement.metrics = [ChartMetricsEnum.volumeOfMentions, ChartMetricsEnum.audienceReach, ChartMetricsEnum.ave];
            }
            if (apiBlock.type === ReportBlockTypeApiEnum.AD_VALUE_EQUIVILANCY) {
                chartDisplayElement.metrics = [ChartMetricsEnum.ave];
            }
            if (apiBlock.type === ReportBlockTypeApiEnum.AUDIENCE_REACH_OVER_TIME) {
                chartDisplayElement.metrics = [ChartMetricsEnum.IMPRESSIONS];
            }
        }
    }
}
